import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Globe, RefreshCw, Search, UserRound } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import CenterLayout from '../layout/CenterLayout';
import AppContext from '../Context/AppContext';
import { useSocket } from '../Context/SocketContext';
import Loader from '../components/Loader';
import { Badge, Confirm, Empty, HScroll, Modal, Pager, Toast } from '../components/userProject/UpUi';
import { dateTime } from '../components/userProject/format';
import { listPlatformPayments, platformPaymentAction } from '../components/paymentSettings/espremium';
import UpUserPanel from '../components/userProject/UpUserPanel';
import { projectApi } from '../components/userProject/api';

const euro = (n) => Number(n || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })

const STATUS = {
    null: { label: 'Lien envoyé', tone: 'info', hint: 'Moyen automatique : en attente du prestataire' },
    wait_ask: { label: 'Infos à envoyer', tone: 'warn', hint: 'Envoie-lui les infos de paiement' },
    wait_info_ok: { label: 'Infos envoyées', tone: 'info', hint: 'En attente de son paiement' },
    paid: { label: 'Payé · à vérifier', tone: 'gold', hint: 'Il dit avoir payé : vérifie puis valide' },
    done: { label: 'Validé', tone: 'ok' },
    refuse: { label: 'Refusé', tone: 'ko' },
    auto_refuse: { label: 'Refusé (prestataire)', tone: 'ko' },
    cancel: { label: 'Annulé', tone: 'muted' },
}
const FILTERS = [['', 'Toutes'], ['pending', 'À traiter'], ['auto', 'Lien envoyé'], ['done', 'Validées'], ['refuse', 'Refusées'], ['cancel', 'Annulées']]
const PLATFORMS = { primestation: 'PrimeStation' }

/** Actions possibles selon le statut. */
const ACTIONS = {
    send_info: { label: 'Envoyer les infos', tone: 'cs' },
    valid: {
        label: 'Valider',
        tone: 'ok',
        confirm: (r) =>
            `${['refuse', 'auto_refuse', 'cancel'].includes(r.status) ? 'Cette demande était refusée ou annulée. ' : ''}Valider le paiement de ${euro(r.price)} ? ${PLATFORMS[r.platform] || r.platform} prolonge le Premium de ${r.email} de ${r.months || 1} mois.`,
    },
    refuse: { label: 'Refuser', tone: 'ko', danger: true, confirm: (r) => `Refuser le paiement de ${r.email} ? Il sera prévenu.` },
    cancel: { label: 'Annuler', tone: 'muted', danger: true, confirm: (r) => `Annuler la demande de ${r.email} ?` },
}
const actionsOf = (status) =>
    status === 'wait_ask' ? ['send_info', 'valid', 'refuse', 'cancel']
        : status === 'paid' ? ['valid', 'refuse']
            : status === null || status === 'wait_info_ok' ? ['valid', 'refuse', 'cancel']
                // refusée / annulée : validable quand même (paiement reçu malgré tout)
                : ['refuse', 'auto_refuse', 'cancel'].includes(status) ? ['valid']
                    : []

/**
 * Paiements des autres plateformes (PrimeStation…) : table platformpaiement d'ESPREMIUM, à part des paiements Empire.
 * À traiter : infos à envoyer (moyens manuels en attente), paiements déclarés à vérifier. Valider → la plateforme
 * prolonge le Premium de son user ; refuser / envoyer les infos → il le voit tout de suite dans le modal Premium.
 * « Fiche » : la fiche complète du user chez sa plateforme (la même que Users projets) pour tout changer depuis ici.
 */
const PlatformPayments = () => {
    const { setCurrentPage } = useContext(AppContext)
    const { socket, setNotifPlatformPay } = useSocket()
    const [filters, setFilters] = useState({ platform: '', status: '', q: '', page: 1 }) // toutes les demandes dès l'ouverture
    const [search, setSearch] = useState('')
    const [data, setData] = useState(null)
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(null)
    const [ask, setAsk] = useState(null) // { row, action } en attente de confirmation
    const [toast, setToast] = useState(null)
    const [openUser, setOpenUser] = useState(null) // { platform, id } : fiche du user ouverte
    const [ips, setIps] = useState(null) // { row, list } : IP de ce paiement et des autres demandes du même client
    const userApi = useMemo(() => (openUser ? projectApi(openUser.platform) : null), [openUser])
    const notify = useCallback((text, isError = false) => setToast({ text, error: isError, at: Date.now() }), [])

    useEffect(() => {
        setCurrentPage('paiements_plateformes')
        setNotifPlatformPay?.(null)
    }, [setCurrentPage, setNotifPlatformPay])

    const load = useCallback(async () => {
        if (!socket) return
        setError('')
        try { setData(await listPlatformPayments(socket, filters)) } catch (e) { setError(e.message) }
    }, [socket, filters])

    // lecture dès que le socket ESPREMIUM est là (et à chaque reconnexion) ; nouvelle demande / « j'ai payé » → relue
    useEffect(() => {
        if (!socket) return
        if (socket.connected) load()
        const onNew = () => { load(); setNotifPlatformPay?.(null) }
        socket.on('connect', load)
        socket.on('notif_platform_paiement', onNew)
        return () => {
            socket.off('connect', load)
            socket.off('notif_platform_paiement', onNew)
        }
    }, [socket, load, setNotifPlatformPay])

    useEffect(() => {
        const t = setTimeout(() => setFilters((f) => (f.q === search.trim() ? f : { ...f, q: search.trim(), page: 1 })), 350)
        return () => clearTimeout(t)
    }, [search])

    // la fenêtre de confirmation se ferme d'elle-même après onConfirm
    const run = async (row, action) => {
        setBusy(row.id)
        try {
            await platformPaymentAction(socket, row.id, action)
            setToast({ text: `${ACTIONS[action].label} : fait (${row.email})`, at: Date.now() })
            await load()
        } catch (e) {
            setToast({ text: e.message, error: true, at: Date.now() })
        }
        setBusy(null)
    }
    const onAction = (row, action) => (ACTIONS[action].confirm ? setAsk({ row, action }) : run(row, action))
    const onToastDone = useCallback(() => setToast(null), [])

    // IP : celle de ce paiement + toutes les demandes du même client (même email, toutes les dates)
    const showIps = async (row) => {
        setIps({ row, list: null })
        try {
            const all = await listPlatformPayments(socket, { platform: row.platform, status: '', q: row.email, page: 1 })
            setIps({ row, list: all.rows.filter((x) => x.email === row.email) })
        } catch (e) {
            setIps(null)
            notify(e.message, true)
        }
    }
    // valeur lue tout de suite (l'événement n'est plus lisible dans la mise à jour différée)
    const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value, page: 1 }))
    const pages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1

    return (
        <MainLayout>
            <CenterLayout>
                <div className='up-page pset'>
                    <header className='up-head'>
                        <div>
                            <h1>Paiements plateformes</h1>
                            <p className='up-muted'>Demandes de paiement des users des autres plateformes (PrimeStation…), à part des paiements Empire.</p>
                        </div>
                    </header>

                    <div className='up-toolbar'>
                        <div className='up-search'>
                            <Search size={16} />
                            <input placeholder='Email, nom ou prénom…' value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
                        </div>
                        <select value={filters.status} onChange={(e) => setFilter('status', e.currentTarget.value)}>
                            {FILTERS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                        </select>
                        <select value={filters.platform} onChange={(e) => setFilter('platform', e.currentTarget.value)}>
                            <option value=''>Toutes les plateformes</option>
                            {Object.entries(PLATFORMS).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                        </select>
                        <button className='btn cw up-icon-btn' onClick={load} title='Actualiser'><RefreshCw size={15} /></button>
                    </div>

                    {error && <p className='up-error'>{error}</p>}
                    {!data && !error ? <div className='up-loading'><Loader /></div> : data && (
                        <>
                            <p className='up-count'><b>{data.total}</b> demande{data.total > 1 ? 's' : ''}</p>
                            <HScroll>
                                <div className='up-table up-table-platformpay'>
                                    <div className='up-thead'><span>Date</span><span>Plateforme</span><span>Client</span><span>Offre · moyen</span><span>Montant</span><span>Statut</span><span>Actions</span></div>
                                    {data.rows.map((r) => {
                                        const st = STATUS[r.status] || { label: r.status, tone: 'muted' }
                                        return (
                                            <div key={r.id} className='up-row pset-payrow'>
                                                <span>{dateTime(r.date)}</span>
                                                <span><Badge tone='admin'>{PLATFORMS[r.platform] || r.platform}</Badge></span>
                                                <span className='pset-client'>
                                                    <b className='truncate'>{[r.firstname, r.lastname].filter(Boolean).join(' ') || '—'}</b>
                                                    <small className='truncate' title={r.email}>{r.email} · user {r.user_id}</small>
                                                </span>
                                                <span className='pset-client'>
                                                    <b>{r.item_label || '—'}</b>
                                                    <small>{r.method}</small>
                                                </span>
                                                <span><b>{euro(r.price)}</b>{r.tax > 0 && <small className='up-muted'> dont {euro(r.tax)} de frais</small>}</span>
                                                <span className='pset-client'>
                                                    <span><Badge tone={st.tone}>{st.label}</Badge></span>
                                                    {(st.hint || r.user_admin) && <small>{r.user_admin && r.user_admin !== 'auto' ? `par ${r.user_admin}` : r.user_admin === 'auto' ? 'automatique' : st.hint}</small>}
                                                </span>
                                                <span className='pset-actions'>
                                                    <button className='pset-act' onClick={() => setOpenUser({ platform: r.platform, id: Number(r.user_id) })} title='Fiche du user : Premium, blocage, mot de passe…'>
                                                        <UserRound size={13} /> Fiche
                                                    </button>
                                                    <button className='pset-act' onClick={() => showIps(r)} title='IP du paiement et des autres demandes de ce client'>
                                                        <Globe size={13} /> IP
                                                    </button>
                                                    {actionsOf(r.status).map((a) => (
                                                        <button key={a} className={`pset-act ${ACTIONS[a].tone}`} disabled={busy === r.id} onClick={() => onAction(r, a)}>
                                                            {ACTIONS[a].label}
                                                        </button>
                                                    ))}
                                                </span>
                                            </div>
                                        )
                                    })}
                                    {!data.rows.length && <Empty>{filters.status === 'pending' ? 'Rien à traiter pour le moment' : 'Aucune demande'}</Empty>}
                                </div>
                            </HScroll>
                            <Pager page={data.page} pages={pages} onPage={(page) => setFilters((f) => ({ ...f, page }))} />
                        </>
                    )}

                    {ask && (
                        <Confirm
                            title={ACTIONS[ask.action].label}
                            text={ACTIONS[ask.action].confirm(ask.row)}
                            action={ACTIONS[ask.action].label}
                            danger={ACTIONS[ask.action].danger}
                            onConfirm={() => run(ask.row, ask.action)}
                            onClose={() => setAsk(null)}
                        />
                    )}
                    {ips && (
                        <Modal wide title={`IP de paiement · ${ips.row.email}`} onClose={() => setIps(null)}>
                            <p className='up-text'>
                                Ce paiement : <code>{ips.row.ip_adress || 'inconnue'}</code>
                            </p>
                            {!ips.list ? <div className='up-loading'><Loader /></div> : (
                                <>
                                    <p className='up-muted pset-ips-sub'>
                                        {ips.list.length} demande{ips.list.length > 1 ? 's' : ''} de ce client · {new Set(ips.list.map((x) => x.ip_adress).filter(Boolean)).size} IP différente{new Set(ips.list.map((x) => x.ip_adress).filter(Boolean)).size > 1 ? 's' : ''}
                                    </p>
                                    <div className='pset-ips'>
                                        {ips.list.map((x) => (
                                            <div key={x.id} className={x.id === ips.row.id ? 'on' : ''}>
                                                <span>{dateTime(x.date)}</span>
                                                <code>{x.ip_adress || '—'}</code>
                                                <span>{euro(x.price)} · {x.method}</span>
                                                <Badge tone={(STATUS[x.status] || {}).tone || 'muted'}>{(STATUS[x.status] || {}).label || x.status}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </Modal>
                    )}
                    {openUser && userApi && (
                        <UpUserPanel
                            key={`${openUser.platform}-${openUser.id}`}
                            pApi={userApi}
                            userId={openUser.id}
                            onClose={() => setOpenUser(null)}
                            onOpen={(id) => setOpenUser((u) => ({ ...u, id }))}
                            notify={notify}
                            onChanged={load}
                        />
                    )}
                    <Toast toast={toast} onDone={onToastDone} />
                </div>
            </CenterLayout>
        </MainLayout>
    );
};

export default PlatformPayments;
